import { useHistory, useParams } from "react-router-dom";
import * as L from "leaflet";
import { useTranslation } from "react-i18next";

import { useScenario } from "src/hooks/useScenario";
import { isDisabled, isReadOnly } from "src/utils/permission.utils";
import { Box, Button, Grid, Typography } from "@mui/material";
import { ResidentialZoneForm, ResidentialZoneFormData } from "src/forms/ResidentialZoneForm";
import { useResidentialZone, useResidentialZoneUpdateMutation } from "src/hooks/residential-zones";

interface Props {
  map: L.Map;
  onClose: () => void;
}

interface Params {
  caseId: string;
  scenarioId: string;
  residentialZoneId: string;
}

export function ResidentialZoneEditPage(props: Props) {
  const { t } = useTranslation();
  const history = useHistory();
  const params = useParams<Params>();
  const { data: scenario } = useScenario(params.scenarioId);
  const { data: residentialZone } = useResidentialZone(params.scenarioId, params.residentialZoneId);
  const updateMutation = useResidentialZoneUpdateMutation(params.scenarioId, params.residentialZoneId);

  async function handleSave(values: ResidentialZoneFormData) {
    await updateMutation.mutateAsync({
      id: params.residentialZoneId,
      ...values,
      totalWeight: 0,
      weightList: [],
    });
  }

  function handleManageResidentialRestrictions() {
    const url = `/cases/${params.caseId}/scenarios/${params.scenarioId}/residential-zones/${params.residentialZoneId}/restrictions`;
    history.push(url);
  }

  return render();

  function render() {
    if (!scenario || !residentialZone) return null;

    return (
      <Box display="flex" flexDirection="column">
        <ResidentialZoneForm
          map={props.map}
          title={t("label.residentialZoneDetails")}
          data={{
            name: residentialZone?.name,
            polygon_frontendSrid: residentialZone.polygon_frontendSrid,
          }}
          isLoading={updateMutation.isLoading}
          errors={updateMutation.error?.response?.data}
          readOnly={isReadOnly(scenario.permissions.updateResidential)}
          onClose={() => props.onClose()}
          onSubmit={handleSave}
        />

        <Box py={3} />

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="h3">{t("label.restrictions")}</Typography>
          </Grid>
          <Grid item xs={12}>
            <Button fullWidth color="primary" variant="text" onClick={handleManageResidentialRestrictions}>
              {isDisabled(scenario.permissions.updateResidential)
                ? t("label.viewResidentialRestrictions")
                : t("label.manageResidentialRestrictions")}
            </Button>
          </Grid>
        </Grid>
      </Box>
    );
  }
}
